import React, { useContext, useState } from "react";
import { Menu, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import MobileBottomNav from "./MobileBottomNav";
import CoPilot from "./CoPilot";
import GlobalFooter from "./GlobalFooter";
import PageAuditWidget from "./PageAuditWidget";
import { EmbeddedLayoutContext } from "./sections/EmbeddedLayout";

export default function AppLayout({ children, title, overline, actions }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  
  // Check if user is admin (has gio@giopilos.com email)
  const userEmail = localStorage.getItem('user_email') || '';
  const isAdmin = userEmail === 'gio@giopilos.com';
  const embedded = useContext(EmbeddedLayoutContext);

  // Inside a section shell the shell already renders the sidebar, header,
  // footer and CoPilot; show only the page's own actions and content.
  if (embedded) {
    return (
      <div data-testid="embedded-page">
        {actions && <div className="mb-4 flex items-center justify-end gap-2 sm:gap-3 flex-wrap">{actions}</div>}
        {children}
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ 
        background: '#000000',
        color: '#FFFFFF',
      }}
    >
      <div className="flex flex-1">
        <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <main className="flex-1 min-w-0 flex flex-col min-h-screen w-full lg:w-auto pb-20 lg:pb-0" data-testid="app-main">
          {(title || overline) && (
            <header
              className="px-4 sm:px-6 md:px-10 pt-6 sm:pt-8 pb-5 sm:pb-7 fade-up bg-black/40 backdrop-blur-xl"
              style={{
                borderBottom: '1px solid rgba(168, 85, 247, 0.3)',
              }}
            >
              <div className="flex items-end justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                  <button
                    onClick={() => setMobileOpen(true)}
                    className="lg:hidden mt-1 p-2 -ml-2 rounded-xl bg-purple-900/20 hover:bg-purple-800/30 transition-all border border-purple-500/20"
                    style={{ color: '#a855f7' }}
                    data-testid="mobile-menu-button"
                    aria-label="Open menu"
                  >
                    <Menu size={22} />
                  </button>
                  <div className="min-w-0">
                    {overline && (
                      <div
                        className="overline mb-1.5 sm:mb-2 truncate uppercase tracking-wider"
                        style={{ fontSize: '0.75rem', color: '#FF10F0', fontWeight: '700', letterSpacing: '0.1em' }}
                        data-testid="page-overline"
                      >
                        {overline}
                      </div>
                    )}
                    {title && (
                      <h1
                        className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter truncate bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text"
                        style={{ color: 'transparent', letterSpacing: '-0.02em' }}
                        data-testid="page-title"
                      >
                        {title}
                      </h1>
                    )}
                  </div>
                </div>
                {actions && <div className="flex items-center gap-2 sm:gap-3 flex-wrap">{actions}</div>}
              </div>
            </header>
          )}
          
          {/* Main content area */}
          <div className="flex-1 px-4 sm:px-6 md:px-10 py-5 sm:py-8 max-w-[1600px]">
            {children}
          </div>
          
          {/* Footer pinned to bottom */}
          <div className="mt-auto">
            <GlobalFooter />
          </div>
        </main>
      </div>
      
      <MobileBottomNav onMenuClick={() => setMobileOpen(true)} />
      
      {/* CoPilot Floating Widget - Available on all admin pages */}
      <CoPilot />
      
      {/* Page Audit Widget - Track pending items per page */}
      <PageAuditWidget />
      
      {/* Admin Pass Button - Always visible with z-index 100 */}
      {isAdmin && (
        <button
          onClick={() => navigate('/gpcrmcontrol')}
          className="fixed top-4 right-4 px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-all hover:scale-105 shadow-2xl"
          style={{
            zIndex: 100,
            background: 'linear-gradient(135deg, #D4AF37 0%, #F4E4C1 100%)',
            color: '#000000',
            border: '2px solid rgba(212, 175, 55, 0.3)',
          }}
          title="Admin Control Panel"
        >
          <ShieldCheck size={16} />
          <span className="hidden sm:inline">Admin Pass</span>
        </button>
      )}
    </div>
  );
}
